import { MapViewerMapbox } from '@onxmaps/map-x-viewer-mapbox'
import type { MapViewerArgs, LonLat } from '@onxmaps/map-x'
import {
  getMaxPitch,
  shouldDisable3d,
  disableDragRotate,
} from '@/services/map-provider/map-config'

/**
 * Options for adding images to the map
 * Based on Mapbox GL JS addImage options
 * @see https://docs.mapbox.com/mapbox-gl-js/api/map/#map#addimage
 */
type ImageOptions = {
  /** Pixel ratio for high DPI displays (default: 1) */
  pixelRatio?: number
  /** Whether to treat image as SDF (signed distance field) for recoloring (default: false) */
  sdf?: boolean
  /** Column ranges that can be stretched horizontally [[x1, x2], ...] */
  stretchX?: Array<[number, number]>
  /** Row ranges that can be stretched vertically [[y1, y2], ...] */
  stretchY?: Array<[number, number]>
  /** Area that can contain text/content [x1, y1, x2, y2] */
  content?: [number, number, number, number]
  /** Error callback invoked when image fails to load (only for string URLs) */
  onError?: (error: Error | null | undefined, imageId: string) => void
}

/**
 * Mapbox MapViewer for Webmap application
 * Consolidates webmap-specific functionality into a single class,
 * serving as a bridge between the current MapboxGL implementation
 * and the Map-X abstraction.
 *
 * Allows for an incremental migration to Map-X while maintaining
 * existing features and customizations in a controlled and singular location.
 */
export class WebmapMapViewerMapbox extends MapViewerMapbox {
  constructor(args: MapViewerArgs) {
    // Current webmap-specific mapbox options
    const webmapMapConstructorArgs = {
      maxPitch: getMaxPitch(shouldDisable3d.value),
      dragRotate: !disableDragRotate.value,
      hash: true,
      attributionControl: false,
    }

    /**
     * Argument spreading hierarchy:
     * 1. args - Base MapViewerArgs from MapManager (includes shared events instance)
     * 2. webmapMapConstructorArgs - Webmap/vertical-specific overrides, eventually spread to `new mapboxgl.Map()` instantiation
     *
     * @see {@link https://github.com/onXmaps/map-x-viewer-mapbox/blob/main/src/index.ts}
     */
    super({
      ...args,
      ...webmapMapConstructorArgs,
    })
  }

  /**
   * @inheritdoc
   */
  public getCameraAltitude(): number {
    const camera = this.map.getFreeCameraOptions()
    if (!camera.position) {
      return 0
    }

    return camera.position.toAltitude()
  }

  /**
   * @inheritdoc
   */
  public setCameraAltitude(altitude: number): void {
    const camera = this.map.getFreeCameraOptions()
    if (!camera.position || typeof camera.position.toLngLat !== 'function') {
      return
    }
    const MercatorCoordinate = camera.position.constructor as unknown as {
      fromLngLat(
        lngLat: { lng: number; lat: number },
        altitude?: number
      ): typeof camera.position
    }
    // Guard: verify the extracted constructor has the expected method
    if (typeof MercatorCoordinate?.fromLngLat !== 'function') {
      return
    }
    const newPosition = MercatorCoordinate.fromLngLat(
      camera.position.toLngLat(),
      altitude
    )

    camera.position = newPosition
    this.map.setFreeCameraOptions(camera)
  }

  /**
   * @inheritdoc
   */
  public getCameraGroundElevation(): number | null {
    const camera = this.map.getFreeCameraOptions()
    if (!camera.position) {
      return null
    }

    const elevation = this.map.queryTerrainElevation(
      camera.position.toLngLat(),
      {
        exaggerated: false,
      }
    )

    return elevation || null
  }

  /**
   * @inheritdoc
   */
  public getCameraPosition(): LonLat | undefined {
    const camera = this.map?.getFreeCameraOptions()
    if (!camera?.position) {
      return undefined
    }
    const lngLat = camera.position.toLngLat()
    return [lngLat.lng, lngLat.lat]
  }

  /**
   * @inheritdoc
   */
  public setFreeCamera(
    center: LonLat,
    altitude: number,
    pitch: number,
    bearing: number
  ): void {
    const camera = this.map?.getFreeCameraOptions()
    if (!camera?.position) {
      return
    }
    const MercatorCoordinate = camera.position.constructor as unknown as {
      fromLngLat(
        lngLat: { lng: number; lat: number },
        altitude?: number
      ): typeof camera.position
    }
    if (typeof MercatorCoordinate?.fromLngLat !== 'function') {
      return
    }
    camera.position = MercatorCoordinate.fromLngLat(
      { lng: center[0], lat: center[1] },
      altitude
    )
    camera.setPitchBearing(pitch, bearing)
    this.map.setFreeCameraOptions(camera)
  }

  /**
   * @inheritdoc
   *
   * Extends the parent implementation with optional exaggeration support,
   * allowing callers to query the visual (exaggerated) terrain height.
   */
  public override queryTerrainElevation(
    lonLat: LonLat,
    options?: { exaggerated?: boolean }
  ): number | undefined {
    const result = this.map?.queryTerrainElevation(lonLat, {
      exaggerated: options?.exaggerated ?? false,
    })
    return result ?? undefined
  }

  /**
   * @inheritdoc
   */
  public setTerrain(
    options: { source?: string; exaggeration?: number } | null
  ): void {
    if (options === null) {
      this.map.setTerrain(null)
      return
    }

    // Get current terrain config to support partial updates
    const currentTerrain = this.map.getTerrain()

    // Determine the source (new or existing)
    const source = options.source ?? currentTerrain?.source

    // Only set terrain if we have a source
    if (!source) {
      return
    }

    // Merge current config with new options
    const terrainConfig = {
      source,
      exaggeration: options.exaggeration ?? currentTerrain?.exaggeration ?? 1.0,
    }

    this.map.setTerrain(terrainConfig)
  }

  /**
   * Add options parameter support for stretchable images to map-x
   * Supports optional error callback for load failures
   * @param id - Unique identifier for the image
   * @param image - Image data or URL to load
   * @param options - Optional configuration for stretchable/SDF images and error handling
   */
  public addImage(
    id: string,
    image: HTMLImageElement | ImageData | ImageBitmap | string,
    options?: ImageOptions
  ): void {
    if (typeof image === 'string') {
      this.map.loadImage(image, (err, img) => {
        if (err || !img) {
          options?.onError?.(err, id)
          return
        }
        this.map.addImage(id, img, options)
      })
    } else {
      this.map.addImage(id, image, options)
    }
  }

  /**
   * Sets the maximum pitch angle constraint (degrees) for the map
   * @param maxPitch - Maximum pitch in degrees (0-85)
   */
  public setMaxPitch(maxPitch: number): void {
    this.map.setMaxPitch(maxPitch)
  }

  /**
   * Sets the minimum pitch angle constraint (degrees) for the map
   * @param minPitch - Minimum pitch in degrees (0-85)
   */
  public setMinPitch(minPitch: number): void {
    this.map.setMinPitch(minPitch)
  }

  /**
   * @inheritdoc
   *
   * Uses MapboxGL's native isMoving() method for real-time handler state checking.
   */
  public isMoving(): boolean {
    return this.map?.isMoving?.() ?? false
  }

  /**
   * @inheritdoc
   *
   * Uses MapboxGL's isStyleLoaded() which stays true once the style is fully loaded.
   *
   * this.map.loaded() is not relied upon as it changes with rendering state.
   * this.map.areTilesLoaded() is not relied upon as it can be a false positive
   * on initial load returning 'true' before tiles are fully loaded and rendered.
   *
   * @returns True if map has completed initial load and tiles are ready
   */
  public isReadyForQueries(): boolean {
    return this.map?.isStyleLoaded?.() ?? false
  }

  /**
   * @inheritdoc
   */
  public override loadLibraryCSS(): void {
    // eslint-disable-next-line no-restricted-imports
    void import(/* webpackMode: "eager" */ 'mapbox-gl/dist/mapbox-gl.css')
  }
}
